import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DefaultTheme } from 'styled-components';

import { HEADER_SETTINGS_DEFAULTS } from 'src/common/constants';
import { NotificationStatusEnum } from 'src/common/constants';
import { filterByEnabledKeys } from 'src/common/helpers/filter-by-keys';
import { jsonToFormData } from 'src/common/helpers/json-to-form-data';
import { DEFAULT_LANGUAGE, DEFAULT_CURRENCY } from 'src/config';
import { changeLanguage } from 'src/config/i18n';
import { ResolutionString } from 'src/lib/charting_library/charting_library';
import { API_ENDPOINTS } from 'src/setup/client/api-endpoints';
import HttpClient from 'src/setup/client/http-client';
import { Theme } from 'src/setup/theme';
import { darkColors, lightColors } from 'src/setup/theme/colors';
import {
    IndexConfig,
    ConfigV2,
    Symbol,
    FutureSymbol,
    TokenRateObj,
    TokenRate,
    UserWallet,
    CoinToken,
    NotificationsResponseDTO,
    WalletTransferable,
    MarginTransferable,
    TransferBalance,
    MarkNotificationAsReadRequestDTO,
} from 'src/types';
import { CommonConfig, SetSettingsRequestDTO } from 'src/types/commonConfig';
import { Token } from 'src/types/configV2';

type WalletTypeNames = {
    [key: number]: string;
};
type HeaderOptionType = {
    favoriteChartTypes: string[];
    activeChartType: number;
    favoriteIntervals: ResolutionString[];
    chartInterval: string;
};

const WALLET_TYPES_NAMES: WalletTypeNames = {
    1: 'SPOT',
    3: 'DERIVATIVES',
    27: 'MARGIN',
};

interface GlobalState {
    loading: Record<string, boolean>;
    error: Record<string, any>;
    theme: DefaultTheme;
    themeMode: 'light' | 'dark';
    indexConfig: IndexConfig;
    configV2: ConfigV2;
    tokenRates: TokenRateObj;
    isAssetsHidden: boolean;
    userWallets: UserWallet[];
    transferAbleAmount: string | number;
    coinTokens: CoinToken;
    derivativeSettings: HeaderOptionType;
    spotSettings: HeaderOptionType;
    commonConfig: CommonConfig;
    rtl: boolean;
    activeLanguage: string;
    activeCurrency: string;
    notifications?: NotificationsResponseDTO;
    popupNotifications?: NotificationsResponseDTO;
    userCurrentFutureTradeAccountIndex: number;
}

const initialState: GlobalState = {
    loading: {},
    error: {},
    theme: Theme,
    themeMode: 'dark',
    indexConfig: {} as IndexConfig,
    configV2: {} as ConfigV2,
    tokenRates: {} as TokenRateObj,
    isAssetsHidden: false,
    transferAbleAmount: 0,
    coinTokens: {} as CoinToken,
    derivativeSettings: HEADER_SETTINGS_DEFAULTS,
    spotSettings: HEADER_SETTINGS_DEFAULTS,
    rtl: localStorage.getItem('rtl') ? String(localStorage.getItem('rtl')) === 'true' : false,
    activeLanguage: localStorage.getItem('activeLanguage') ?? DEFAULT_LANGUAGE,
    activeCurrency: localStorage.getItem('activeCurrency') ?? DEFAULT_CURRENCY,
    notifications: undefined,
    popupNotifications: undefined,
    userCurrentFutureTradeAccountIndex: undefined,
} as GlobalState;

// Async thunk actions and status keys
export const AsyncActionKeys = {
    fetchIndexConfig: 'fetchIndexConfig',
    fetchConfigV2: 'fetchConfigV2',
    fetchTokenRates: 'fetchTokenRates',
    fetchWalletSubAccounts: 'fetchWalletSubAccounts',
    fetchTransferAvailable: 'fetchTransferAvailable',
    fetchTransferAvailableMargin: 'fetchTransferAvailableMargin',
    transferWalletBalance: 'transferWalletBalance',
    fetchCoinTokens: 'fetchCoinTokens',
    getSettings: 'getSettings',
    setSettings: 'setSettings',
    setActiveLanguage: 'setActiveLanguage',
    refreshCommonConfigFromStorage: 'refreshCommonConfigFromStorage',
    getNotifications: 'getNotifications',
    getPopupNotifications: 'getPopupNotifications',
    markNotificationAsRead: 'markNotificationAsRead',
    getServerTime: 'getServerTime',
    klineHistory: 'klineHistory',
    indexKlineHistory: 'indexKlineHistory',
};

export const fetchIndexConfig = createAsyncThunk(
    AsyncActionKeys.fetchIndexConfig,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(API_ENDPOINTS.INDEX_CONFIG);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchConfigV2 = createAsyncThunk(
    AsyncActionKeys.fetchConfigV2,
    async (_, thunkAPI) => {
        const queryParams: string =
            'custom_keys=loginReg,analytics&callback=JSON.stringify&tab=exchange&type=all&platform=1&without_country=true';

        try {
            const response = await HttpClient.get(`${API_ENDPOINTS.CONFIG_V2}?${queryParams}`, {
                headers: {
                    Accept: '*', // TODO:: Remove custom header when API response fixed from backend
                },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchTokenRates = createAsyncThunk(
    AsyncActionKeys.fetchTokenRates,
    async (payload: object, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.TOKEN_RATES, payload);
            return response.data.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchWalletSubAccounts = createAsyncThunk(
    AsyncActionKeys.fetchWalletSubAccounts,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(`${API_ENDPOINTS.WALLETS_SUB_ACCOUNTS}`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchTransferAvailable = createAsyncThunk(
    AsyncActionKeys.fetchTransferAvailable,
    async (body: WalletTransferable, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.TRANSFER_AVAILABLE}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchTransferAvailableMargin = createAsyncThunk(
    AsyncActionKeys.fetchTransferAvailableMargin,
    async (body: MarginTransferable, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.MARGIN_TRANSFER_AVAILABLE}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const transferWalletBalance = createAsyncThunk(
    AsyncActionKeys.transferWalletBalance,
    async (body: TransferBalance, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.TRANSFER_BALANCE}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchCoinTokens = createAsyncThunk(
    AsyncActionKeys.fetchCoinTokens,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(`${API_ENDPOINTS.WALLET_COIN_TOKENS}`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getSettings = createAsyncThunk(AsyncActionKeys.getSettings, async (_, thunkAPI) => {
    try {
        const response = await HttpClient.get(API_ENDPOINTS.GET_SETTINGS);
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const setSettings = createAsyncThunk(
    AsyncActionKeys.setSettings,
    async (data: SetSettingsRequestDTO, thunkAPI) => {
        try {
            const formDataBody = jsonToFormData(data);
            HttpClient.post(API_ENDPOINTS.SET_SETTINGS, formDataBody);
            return data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const setActiveLanguage = createAsyncThunk(
    AsyncActionKeys.setActiveLanguage,
    async (lang: string, thunkAPI) => {
        try {
            await changeLanguage(lang);
            return lang;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const refreshCommonConfigFromStorage = createAsyncThunk(
    AsyncActionKeys.refreshCommonConfigFromStorage,
    async (_, thunkAPI) => {
        try {
            const lang = localStorage.getItem('activeLanguage');
            const unit = localStorage.getItem('activeCurrency');
            if (lang) {
                await changeLanguage(lang);
            }
            return { lang, unit };
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getNotifications = createAsyncThunk(
    AsyncActionKeys.getNotifications,
    async (_, thunkAPI) => {
        try {
            const data = { page: 1, size: 10 };
            const formDataBody = jsonToFormData(data);
            const response = await HttpClient.post(API_ENDPOINTS.GET_NOTIFICATIONS, formDataBody);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getPopupNotifications = createAsyncThunk(
    AsyncActionKeys.getPopupNotifications,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.GET_POPUP_NOTIFICATIONS, {});
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const markNotificationAsRead = createAsyncThunk(
    AsyncActionKeys.markNotificationAsRead,
    async (data: MarkNotificationAsReadRequestDTO, thunkAPI) => {
        try {
            const formDataBody = jsonToFormData(data);
            const response = await HttpClient.post(
                API_ENDPOINTS.MARK_NOTIFICATION_AS_READ,
                formDataBody,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getServerTime = () => {
    return HttpClient.get(`${API_ENDPOINTS.SERVER_TIME}`);
};

export const klineHistory = (queryParams: string) => {
    return HttpClient.get(`${API_ENDPOINTS.KLINE_HISTORY}?${queryParams}`);
};

export const indexKlineHistory = (queryParams: string) => {
    return HttpClient.get(`${API_ENDPOINTS.INDEX_KLINE_HISTORY}?${queryParams}`);
};

//slice
export const globalSlice = createSlice({
    name: 'globalSlice',
    initialState,
    reducers: {
        updateThemeData: (state, action) => {
            const { isDark } = action.payload;
            if (isDark !== undefined) {
                let colors = darkColors;
                let themeMode: 'light' | 'dark' = 'dark';
                if (!isDark) {
                    colors = lightColors;
                    themeMode = 'light';
                }
                state.theme = { ...state.theme, colors };
                state.themeMode = themeMode;
            }
            state.theme = { ...state.theme, ...action.payload };
        },
        updateHiddenAsset: (state, action) => {
            state.isAssetsHidden = action.payload;
        },
        updateFavoriteChartTypes: (state, action) => {
            const { type, chartType } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;
            const favoriteIntervals = chartState.favoriteChartTypes;
            return {
                ...state,
                [type]: {
                    ...chartState,
                    favoriteChartTypes: favoriteIntervals.includes(chartType)
                        ? favoriteIntervals.filter((id) => id !== chartType)
                        : [...favoriteIntervals, chartType],
                },
            };
        },
        updateChartType: (state, action) => {
            const { type, chartId } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;
            return {
                ...state,
                [type]: {
                    ...chartState,
                    activeChartType: chartId,
                },
            };
        },
        updateChartInterval: (state, action) => {
            const { type, interval } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;
            return {
                ...state,
                [type]: {
                    ...chartState,
                    chartInterval: interval,
                },
            };
        },
        updateFavoriteIntervals: (state, action) => {
            const { type, resolutions } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;

            return {
                ...state,
                [type]: {
                    ...chartState,
                    favoriteIntervals: resolutions,
                },
            };
        },
        toggleRtl: (state, action) => {
            const value = action.payload;

            state.rtl = value;
            localStorage.setItem('rtl', String(value));
        },
        setActiveCurrency: (state, action) => {
            const value = action.payload;

            state.activeCurrency = value;
            localStorage.setItem('activeCurrency', value);
        },
        updateGlobalConfigSymbols: (state, action: PayloadAction<string[]>) => {
            const enabled = action.payload ?? [];

            const enabledCustomQuoteTokens = enabled.length
                ? (state.configV2?.customQuoteToken ?? []).filter(
                      (t) => t.tokenId === 'USDT' || enabled.includes(`${t.tokenId}USDT`),
                  )
                : [];
            const enabledQuoteTokens = enabled.length
                ? (state.configV2?.quoteToken ?? []).filter(
                      (t) => t.tokenId === 'USDT' || enabled.includes(`${t.tokenId}USDT`),
                  )
                : [];

            state.configV2 = {
                ...state.configV2,
                symbol: filterByEnabledKeys(state.configV2?.symbol, enabled),
                futuresSymbol: filterByEnabledKeys(state.configV2?.futuresSymbol, enabled),
                token: filterByEnabledKeys(state.configV2?.token, enabled, ['USDT', '-SWAP-USDT']),
                quoteToken: enabledQuoteTokens.map((t) => {
                    return {
                        ...t,
                        quoteTokenSymbols: (t.quoteTokenSymbols ?? []).filter((s) =>
                            enabled.includes(s.symbolId),
                        ),
                    };
                }),
                customQuoteToken: enabledCustomQuoteTokens.map((t) => {
                    return {
                        ...t,
                        quoteTokenSymbols: (t.quoteTokenSymbols ?? []).filter((s) =>
                            enabled.includes(s.symbolId),
                        ),
                    };
                }),
            };
        },
        changeFutureTradingAccount: (state, action) => {
            state.userCurrentFutureTradeAccountIndex = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchIndexConfig.fulfilled, (state, action) => {
                state.indexConfig = action.payload;
            })
            .addCase(fetchConfigV2.fulfilled, (state, action) => {
                let data = action.payload;
                data = data.split('JSON.stringify(')[1];
                data = JSON.parse(data.substr(0, data.length - 1));

                let symbols: object = {};
                data.symbol.map((item: Symbol) => {
                    symbols = { ...symbols, [item.symbolId]: item };
                });
                let futuresSymbol: object = {};
                data.futuresSymbol.map((item: FutureSymbol) => {
                    futuresSymbol = { ...futuresSymbol, [item.symbolId]: item };
                });
                let tokens: object = {};
                data.token.map((item: Token) => {
                    tokens = { ...tokens, [item.tokenId]: item };
                });
                data['symbol'] = symbols;
                data['futuresSymbol'] = futuresSymbol;
                data['token'] = tokens;
                state.configV2 = data;
            })
            .addCase(fetchTokenRates.fulfilled, (state, action) => {
                let tokenRates = {} as TokenRateObj;
                action.payload.map((item: TokenRate) => {
                    tokenRates = { ...tokenRates, [item.token]: item.rates };
                });
                state.tokenRates = tokenRates;
            })
            .addCase(fetchWalletSubAccounts.fulfilled, (state, action) => {
                const userWallets: UserWallet[] = action.payload;
                state.userWallets = userWallets.map((wallet: UserWallet) => {
                    wallet.accountName = WALLET_TYPES_NAMES[wallet.accountType];
                    return wallet;
                });
                state.userWallets = action.payload;
            })
            .addCase(fetchTransferAvailable.pending, (state) => {
                state.transferAbleAmount = 0;
            })
            .addCase(fetchTransferAvailable.fulfilled, (state, action) => {
                state.transferAbleAmount = action.payload.amount;
            })
            .addCase(fetchTransferAvailableMargin.pending, (state) => {
                state.transferAbleAmount = 0;
            })
            .addCase(fetchTransferAvailableMargin.fulfilled, (state, action) => {
                state.transferAbleAmount = action.payload.availWithdrawAmount;
            })
            .addCase(fetchCoinTokens.fulfilled, (state, action) => {
                state.coinTokens = action.payload;
            })
            .addCase(getSettings.fulfilled, (state, action) => {
                const response = action.payload?.commonConfig
                    ? JSON.parse(action.payload.commonConfig)
                    : {};
                state.commonConfig = response;
            })
            .addCase(setSettings.fulfilled, (state, action) => {
                const response = JSON.parse(action.meta.arg.common_config);
                state.commonConfig = response;
            })
            .addCase(setActiveLanguage.fulfilled, (state, action) => {
                const lang = action.payload;

                state.activeLanguage = lang;
                localStorage.setItem('activeLanguage', lang);
            })
            .addCase(refreshCommonConfigFromStorage.fulfilled, (state, action) => {
                const { lang, unit } = action.payload;
                if (lang) {
                    state.activeLanguage = lang;
                }
                if (unit) {
                    state.activeCurrency = unit;
                }
            })
            .addCase(getNotifications.fulfilled, (state, action) => {
                state.notifications = action.payload;
            })
            .addCase(getPopupNotifications.fulfilled, (state, action) => {
                state.popupNotifications = action.payload;
            })
            .addCase(markNotificationAsRead.fulfilled, (state, action) => {
                const id = action.payload.notification_id;
                const current = state.popupNotifications ?? {
                    userNotifications: [],
                    total: 0,
                    unread: 0,
                };

                state.popupNotifications = {
                    ...current,
                    userNotifications: current.userNotifications.map((notification) =>
                        notification.id === id
                            ? { ...notification, status: NotificationStatusEnum.READ }
                            : notification,
                    ),
                };
            });

        // Loading and error handling for async actions
        builder.addMatcher(
            (action) => action.type.endsWith('/pending'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = true;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/fulfilled'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = undefined;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/rejected'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = action.payload;
            },
        );
    },
});

export const {
    updateChartInterval,
    updateChartType,
    updateFavoriteChartTypes,
    updateFavoriteIntervals,
    updateHiddenAsset,
    updateThemeData,
    toggleRtl,
    setActiveCurrency,
    updateGlobalConfigSymbols,
    changeFutureTradingAccount,
} = globalSlice.actions;
